import { createModel } from '@rematch/core';
import { post } from 'utils/http';
import type {
  IEvaluateState,
  ISampleFormData,
  ISampleSetFormData,
  IEvalTaskFormData,
  IEvalDimensionFormData,
  ILinkSamplesParams,
  IEvaluateListParams,
  IBenchmarkDimensionScore,
  GSBEvalResultValue,
  ICreateListenTaskParams,
  IGetBotListParams,
  ICloneSampleSetParams,
  IGetEvaluatorEvalRecordsReq,
  IPromptFormData,
} from 'types/evaluate';

const initialState: IEvaluateState = {
  sampleList: [],
  sampleTotal: 0,
  sampleSetList: [],
  sampleSetTotal: 0,
  taskList: [],
  taskTotal: 0,
  dimensionList: [],
  dimensionTotal: 0,
  listenTaskList: [],
  listenTaskTotal: 0,
  promptList: [],
  promptTotal: 0,
  loading: false,
};

export default createModel()({
  state: initialState,
  reducers: {
    setState(state: IEvaluateState, payload: Partial<IEvaluateState>) {
      return { ...state, ...payload };
    },
    setLoading(state: IEvaluateState, loading: boolean) {
      return { ...state, loading };
    },
  },
  effects: (dispatch: any) => ({
    // ==================== 样本管理 ====================
    /**
     * 获取样本列表
     * POST /api/evaluate/getSampleList
     */
    async getSampleList(params: IEvaluateListParams = {}) {
      dispatch.evaluate.setLoading(true);
      try {
        const result = await post('/api/evaluate/getSampleList', params);
        if (result) {
          dispatch.evaluate.setState({
            sampleList: result.list || [],
            sampleTotal: result.total || 0,
          });
        }
        return result || {};
      } finally {
        dispatch.evaluate.setLoading(false);
      }
    },

    /**
     * 获取所有样本（不分页，用于下拉选择）
     * POST /api/evaluate/getAllSamples
     */
    async getAllSamples() {
      const result = await post('/api/evaluate/getAllSamples', {});
      return result?.list || [];
    },

    /**
     * 根据业务类型获取所有样本（不分页，用于样本的选择）
     * POST /api/evaluate/getAllSamplesByBusiness
     */
    async getAllSamplesByBusiness(business: number) {
      const result = await post('/api/evaluate/getAllSamplesByBusiness', { business });
      return result?.list || [];
    },

    /**
     * 创建样本
     * POST /api/evaluate/createSample
     */
    async createSample(data: ISampleFormData) {
      const result = await post('/api/evaluate/createSample', data);
      return result;
    },

    /**
     * 更新样本
     * POST /api/evaluate/updateSample
     */
    async updateSample(data: ISampleFormData) {
      const result = await post('/api/evaluate/updateSample', data);
      return result;
    },

    /**
     * 删除样本
     * POST /api/evaluate/deleteSample
     */
    async deleteSample(id: number) {
      const result = await post('/api/evaluate/deleteSample', { id });
      return result;
    },

    /**
     * 批量导入样本
     * POST /api/evaluate/importSamples
     */
    async importSamples(data: { file: File; business: number }) {
      const formData = new FormData();
      formData.append('file', data.file);
      formData.append('business', String(data.business));
      const result = await post('/api/evaluate/importSamples', {}, { formData });
      return result;
    },

    // ==================== 样本集管理 ====================
    /**
     * 获取样本集列表
     * POST /api/evaluate/getSampleSetList
     */
    async getSampleSetList(params: IEvaluateListParams = {}) {
      dispatch.evaluate.setLoading(true);
      try {
        const result = await post('/api/evaluate/getSampleSetList', params);
        if (result) {
          dispatch.evaluate.setState({
            sampleSetList: result.list || [],
            sampleSetTotal: result.total || 0,
          });
        }
        return result || {};
      } finally {
        dispatch.evaluate.setLoading(false);
      }
    },

    /**
     * 创建样本集
     * POST /api/evaluate/createSampleSet
     */
    async createSampleSet(data: ISampleSetFormData) {
      const result = await post('/api/evaluate/createSampleSet', data);
      return result;
    },

    /**
     * 更新样本集
     * POST /api/evaluate/updateSampleSet
     */
    async updateSampleSet(data: ISampleSetFormData) {
      const result = await post('/api/evaluate/updateSampleSet', data);
      return result;
    },

    /**
     * 删除样本集
     * POST /api/evaluate/deleteSampleSet
     */
    async deleteSampleSet(id: number) {
      const result = await post('/api/evaluate/deleteSampleSet', { id });
      return result;
    },

    /**
     * 获取样本集内的样本列表（分页）
     * POST /api/evaluate/getSamplesBySetId
     */
    async getSamplesBySetId(params: IEvaluateListParams = {}) {
      const result = await post('/api/evaluate/getSamplesBySetId', params);
      return result || {};
    },

    /**
     * 关联样本到样本集
     * POST /api/evaluate/linkSamples
     */
    async linkSamples(params: ILinkSamplesParams) {
      const result = await post('/api/evaluate/linkSamples', params);
      return result;
    },

    /**
     * 解除样本关联
     * POST /api/evaluate/unlinkSamples
     */
    async unlinkSamples(params: ILinkSamplesParams) {
      const result = await post('/api/evaluate/unlinkSamples', params);
      return result;
    },

    /**
     * 获取样本关联的样本集ID列表
     * POST /api/evaluate/getSampleSetIdsBySampleId
     */
    async getSampleSetIdsBySampleId(sampleId: number) {
      const result = await post('/api/evaluate/getSampleSetIdsBySampleId', { sampleId });
      return result?.setIds || [];
    },

    /**
     * 获取所有样本集（不分页，用于下拉选择）
     * POST /api/evaluate/getAllSampleSets
     */
    async getAllSampleSets() {
      const result = await post('/api/evaluate/getAllSampleSets', {});
      return result?.list || [];
    },

    /**
     * 根据类型获取所有样本集（不分页，用于样本的配置）
     * POST /api/evaluate/getAllSampleSetsByType
     */
    async getAllSampleSetsByType(type: number) {
      const result = await post('/api/evaluate/getAllSampleSetsByType', { type });
      return result?.list || [];
    },

    /**
     * 复刻样本集（替换样本内容中的 agent_id）
     * POST /api/evaluate/cloneSampleSet
     */
    async cloneSampleSet(data: ICloneSampleSetParams) {
      const result = await post('/api/evaluate/cloneSampleSet', data);
      return result;
    },

    // ==================== 评估任务管理 ====================
    /**
     * 获取任务列表
     * POST /api/evaluate/getTaskList
     */
    async getModelList(params: IEvaluateListParams = {}) {
      const result = await post('/api/evaluate/getModelList', params);
      return result || {};
    },

    /**
     * 获取多个样本集内的样本列表
     * POST /api/evaluate/getSamplesBySetIds
     */
    async getSamplesBySetIds(sampleSetIds: number[]) {
      const result = await post('/api/evaluate/getSamplesBySetIds', { sampleSetIds });
      return result?.list || [];
    },

    /**
     * 获取任务列表
     * POST /api/evaluate/getTaskList
     */
    async getTaskList(params: IEvaluateListParams = {}) {
      dispatch.evaluate.setLoading(true);
      try {
        const result = await post('/api/evaluate/getTaskList', params);
        if (result) {
          dispatch.evaluate.setState({
            taskList: result.list || [],
            taskTotal: result.total || 0,
          });
        }
        return result || {};
      } finally {
        dispatch.evaluate.setLoading(false);
      }
    },

    /**
     * 任务重试（模型重试 / 机评重试）
     * POST /api/evaluate/restartGenerateTask
     * @param params.taskId 任务 ID（前端未区分 generate/eval id，统一传同一个 taskId）
     * @param params.type 重试类型，见 RestartType 枚举
     */
    async restartGenerateTask(params: { taskId: number; type: number }) {
      const result = await post('/api/evaluate/restartGenerateTask', params);
      return result;
    },

    /**
     * 创建评估任务
     * POST /api/evaluate/createTask
     */
    async createTask(data: IEvalTaskFormData) {
      const result = await post('/api/evaluate/createTask', data);
      return result;
    },

    /**
     * 更新评估任务
     * POST /api/evaluate/updateTask
     */
    async updateTask(data: IEvalTaskFormData) {
      const result = await post('/api/evaluate/updateTask', data);
      return result;
    },

    /**
     * 更新评估任务的 ext 扩展字段（仅写 JSON 字符串）
     * POST /api/evaluate/updateTask
     */
    async updateTaskExt(params: { id: number; ext: string }) {
      const result = await post('/api/evaluate/updateTask', params);
      return result;
    },

    /**
     * 删除评估任务
     * POST /api/evaluate/deleteTask
     */
    async deleteTask(id: number) {
      const result = await post('/api/evaluate/deleteTask', { id });
      return result;
    },

    // ==================== 评估维度管理 ====================
    /**
     * 获取评估维度列表
     * POST /api/evaluate/getDimensionList
     */
    async getDimensionList(params: IEvaluateListParams = {}) {
      dispatch.evaluate.setLoading(true);
      try {
        const result = await post('/api/evaluate/getDimensionList', params);
        if (result) {
          dispatch.evaluate.setState({
            dimensionList: result.list || [],
            dimensionTotal: result.total || 0,
          });
        }
        return result || {};
      } finally {
        dispatch.evaluate.setLoading(false);
      }
    },

    /**
     * 创建评估维度
     * POST /api/evaluate/createDimension
     */
    async createDimension(data: IEvalDimensionFormData) {
      const result = await post('/api/evaluate/createDimension', data);
      return result;
    },

    /**
     * 更新评估维度
     * POST /api/evaluate/updateDimension
     */
    async updateDimension(data: IEvalDimensionFormData) {
      const result = await post('/api/evaluate/updateDimension', data);
      return result;
    },

    /**
     * 删除评估维度
     * POST /api/evaluate/deleteDimension
     */
    async deleteDimension(id: number) {
      const result = await post('/api/evaluate/deleteDimension', { id });
      return result;
    },

    /**
     * 获取所有评估维度（不分页，用于下拉选择）
     * POST /api/evaluate/getAllDimensions
     */
    async getAllDimensions() {
      const result = await post('/api/evaluate/getAllDimensions', {});
      return result?.list || [];
    },

    // ==================== GSB盲评 ====================
    /**
     * 获取GSB盲评数据列表
     * POST /api/evaluate/getGSBEvalList
     */
    async getGSBEvalList(taskId: number) {
      const result = await post('/api/evaluate/getGSBEvalList', { taskId });
      return result || { evalList: [], progress: { total: 0, evaluated: 0, currentIndex: 0 } };
    },

    /**
     * 提交GSB盲评结果
     * POST /api/evaluate/submitGSBEvalResult
     * @param isFinal - 是否为最终提交（true: 触发状态变更，false: 临时保存）
     */
    async submitGSBEvalResult(params: {
      taskId: number;
      evalItemId: number;
      result: GSBEvalResultValue;
      reason?: string;
      isFinal?: boolean;
    }) {
      const result = await post('/api/evaluate/submitGSBEvalResult', params);
      return result;
    },

    /**
     * 获取盲评评测统计
     * POST /api/evaluate/getGSBEvalStats
     */
    async getGSBEvalStats(taskId: number) {
      const result = await post('/api/evaluate/getGSBEvalStats', { taskId });
      return result;
    },

    // ==================== Benchmark评估 ====================
    /**
     * 获取Benchmark评估数据列表
     * POST /api/evaluate/getBenchmarkEvalList
     */
    async getBenchmarkEvalList(taskId: number) {
      const result = await post('/api/evaluate/getBenchmarkEvalList', { taskId });
      return (
        result || {
          evalList: [],
          evalResults: [],
          dimensions: [],
          progress: { total: 0, evaluated: 0, currentIndex: 0 },
          isFinish: false,
          machineEvalProgress: null,
        }
      );
    },

    /**
     * 提交Benchmark评估结果
     * POST /api/evaluate/submitBenchmarkEvalResult
     */
    async submitBenchmarkEvalResult(params: {
      taskId: number;
      evalItemId: number;
      dimensionScores: IBenchmarkDimensionScore[];
      comment?: string;
    }) {
      const result = await post('/api/evaluate/submitBenchmarkEvalResult', params);
      return result;
    },

    /**
     * 获取Benchmark评测统计
     * POST /api/evaluate/getBenchmarkEvalStats
     */
    async getBenchmarkEvalStats(taskId: number) {
      const result = await post('/api/evaluate/getBenchmarkEvalStats', { taskId });
      return result;
    },

    // ==================== 机评进度 & 重试（TODO: 接入真实接口后替换 mock） ====================
    /**
     * 获取机评进度（TODO: 替换为 POST /api/evaluate/getMachineEvalProgress）
     */
    async getMachineEvalProgress(_taskId: number) {
      // TODO: 接入真实接口，当前返回 mock 占位数据
      return {
        total: 120,
        evaluated: 78,
        percent: 65,
        status: 2, // 执行中（复用 MODEL_EXECUTE_STATUS_MAP）
        errorMessage: '',
      };
    },

    /**
     * 获取评测者的评测记录（管理员鉴权）
     * POST /api/evaluate/getEvaluatorEvalRecords
     */
    async getEvaluatorEvalRecords(params: IGetEvaluatorEvalRecordsReq) {
      const result = await post('/api/evaluate/getEvaluatorEvalRecords', params);
      return result;
    },

    // ==================== 模型结果图片管理 ====================
    /**
     * 获取模型结果图片列表
     * POST /api/evaluate/getModelResults
     */
    async getModelResults(params: { taskId: number; modelId: string }) {
      const result = await post('/api/evaluate/getModelResults', params);
      return result;
    },

    /**
     * 导入模型结果（支持 Excel / ZIP，可同时上传）
     * POST /api/evaluate/importModelResults
     * @param file - Excel 文件（可选）
     * @param zipFile - ZIP 媒体文件（可选）
     * @description 至少需传入 file 或 zipFile 之一
     */
    async importModelResults(params: { taskId: number; modelId: string; file?: File; zipFile?: File }) {
      const formData = new FormData();
      if (params.file) {
        formData.append('file', params.file);
      }
      if (params.zipFile) {
        formData.append('zipFile', params.zipFile);
      }
      formData.append('taskId', String(params.taskId));
      formData.append('modelId', params.modelId);
      const result = await post('/api/evaluate/importModelResults', {}, { formData });
      return result;
    },

    /**
     * 导入模型结果 ZIP（音频文件，保留老接口）
     * POST /api/evaluate/importModelZipResults
     */
    async importModelZipResults(params: { taskId: number; modelId: string; file: File }) {
      const formData = new FormData();
      formData.append('file', params.file);
      formData.append('taskId', String(params.taskId));
      formData.append('modelId', params.modelId);
      const result = await post('/api/evaluate/importModelZipResults', {}, { formData });
      return result;
    },

    // ==================== 监听任务管理 ====================
    /**
     * 获取 Bot 列表（分页）
     * POST /api/evaluate/getBotList
     */
    async getBotList(params: IGetBotListParams) {
      const result = await post('/api/evaluate/getBotList', params);
      return result || { list: [], total: 0 };
    },

    /**
     * 创建监听任务
     * POST /api/evaluate/createListenTask
     */
    async createListenTask(params: ICreateListenTaskParams) {
      const result = await post('/api/evaluate/createListenTask', params);
      return result;
    },

    /**
     * 停止监听任务
     * POST /api/evaluate/stopListenTask
     */
    async stopListenTask(taskId: number) {
      const result = await post('/api/evaluate/stopListenTask', { taskId });
      return result;
    },

    /**
     * 查询所有监听任务
     * POST /api/evaluate/getListenTaskList
     */
    async getListenTaskList(params: IEvaluateListParams) {
      dispatch.evaluate.setLoading(true);
      try {
        const result = await post('/api/evaluate/getListenTaskList', params);
        if (result) {
          dispatch.evaluate.setState({
            listenTaskList: result.tasks || [],
            listenTaskTotal: result.total || 0,
          });
        }
        return result || {};
      } finally {
        dispatch.evaluate.setLoading(false);
      }
    },

    // ==================== Prompt 管理 ====================
    /**
     * 获取 Prompt 列表
     * POST /api/evaluate/getPromptList
     */
    async getPromptList(params: IEvaluateListParams = {}) {
      dispatch.evaluate.setLoading(true);
      try {
        const result = await post('/api/evaluate/getPromptList', params);
        if (result) {
          dispatch.evaluate.setState({
            promptList: result.list || [],
            promptTotal: result.total || 0,
          });
        }
        return result || {};
      } finally {
        dispatch.evaluate.setLoading(false);
      }
    },

    /**
     * 获取所有 Prompt（不分页，用于下拉选择）
     * POST /api/evaluate/getAllPrompts
     */
    async getAllPrompts() {
      const result = await post('/api/evaluate/getAllPrompts', {});
      return result?.list || [];
    },

    /**
     * 创建 Prompt
     * POST /api/evaluate/createPrompt
     */
    async createPrompt(data: IPromptFormData) {
      const result = await post('/api/evaluate/createPrompt', data);
      return result;
    },

    /**
     * 更新 Prompt
     * POST /api/evaluate/updatePrompt
     */
    async updatePrompt(data: IPromptFormData) {
      const result = await post('/api/evaluate/updatePrompt', data);
      return result;
    },

    /**
     * 删除 Prompt
     * POST /api/evaluate/deletePrompt
     */
    async deletePrompt(id: number) {
      const result = await post('/api/evaluate/deletePrompt', { id });
      return result;
    },
  }),
});
